import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RegistrationInquiryService } from '../../service/registration-inquiry.service';
import { RegistrationInquiryMessage } from '../../models/RegistrationInquiry';
import { containsProfanity, PROFANITY_MESSAGE } from '../../util/profanity';
import {
  containsLink,
  IMAGE_ACCEPT,
  IMAGE_ONLY_MESSAGE,
  isImageAttachment,
  LINK_MESSAGE,
} from '../../util/chat-content';
import {
  guestChatStorage,
  GUEST_NAME_KEY,
  INQUIRY_ID_KEY,
  INQUIRY_TOKEN_KEY,
} from '../../util/session-keys';

const HELP_EMOJIS = [
  '😀', '😄', '😊', '🙂', '😉', '😍', '🤗', '🤔',
  '👍', '👎', '👋', '🙏', '👏', '💪', '🤝', '✌️',
  '❤️', '🎉', '✅', '❌', '⭐', '🔥', '💡', '📌',
  '📄', '📎', '📅', '⏰', '💼', '🏢', '💰', '❓',
];

@Component({
  selector: 'app-registration-help',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './registration-help.component.html',
  styleUrls: ['./registration-help.component.css'],
})
export class RegistrationHelpComponent implements OnInit, OnDestroy {
  @ViewChild('messageScroll') messageScroll: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  readonly emojis = HELP_EMOJIS;
  readonly reactionOptions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
  readonly imageAccept = IMAGE_ACCEPT;
  reactionPickerFor: number | null = null;

  open = false;
  emojiOpen = false;
  messages: RegistrationInquiryMessage[] = [];
  unreadCount = 0;
  sending = false;

  guestName = '';
  guestEmail = '';
  newMessageText = '';
  startError = '';
  pendingFile: File | null = null;

  private inquiryId: string | null = null;
  private guestToken: string | null = null;
  private pollHandle: any;
  private readonly openPollIntervalMs = 5000;
  private readonly closedPollIntervalMs = 30000;

  constructor(
    private registrationInquiryService: RegistrationInquiryService,
    private router: Router,
  ) {}

  get hasInquiry(): boolean {
    return !!(this.inquiryId && this.guestToken);
  }

  get storedName(): string {
    return guestChatStorage.getItem(GUEST_NAME_KEY) || 'there';
  }

  get signedIn(): boolean {
    try {
      return JSON.parse(localStorage.getItem('login') || 'false') === true;
    } catch {
      return false;
    }
  }

  get signedInEmail(): string {
    try {
      return JSON.parse(localStorage.getItem('userEmail') || '""') || '';
    } catch {
      return '';
    }
  }

  ngOnInit(): void {
    this.inquiryId = guestChatStorage.getItem(INQUIRY_ID_KEY);
    this.guestToken = guestChatStorage.getItem(INQUIRY_TOKEN_KEY);
    if (this.hasInquiry && !this.signedIn) {
      this.refresh();
      this.startPolling();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  toggle(): void {
    this.open = !this.open;
    this.emojiOpen = false;
    if (this.hasInquiry && !this.signedIn) {
      if (this.open) {
        this.refresh();
      }
      this.startPolling();
    }
  }

  openChat(): void {
    this.open = false;
    this.router.navigate(['/vendor-chat']);
  }

  toggleEmoji(): void {
    this.emojiOpen = !this.emojiOpen;
  }

  addEmoji(emoji: string): void {
    this.newMessageText = `${this.newMessageText}${emoji}`;
  }

  onComposerInput(): void {
    if (this.startError) {
      this.startError = '';
    }
  }

  toggleReactionPicker(messageId: number): void {
    this.reactionPickerFor = this.reactionPickerFor === messageId ? null : messageId;
  }

  react(message: RegistrationInquiryMessage, emoji: string): void {
    if (!this.hasInquiry) return;
    const next = message.guestReaction === emoji ? null : emoji;
    this.reactionPickerFor = null;
    this.registrationInquiryService
      .setReaction(this.inquiryId!, this.guestToken!, message.id, next)
      .subscribe({
        next: (updated) => {
          message.guestReaction = updated.guestReaction;
          message.staffReaction = updated.staffReaction;
        },
        error: () => {
          this.startError = 'Unable to update the reaction. Please try again.';
        },
      });
  }

  pickFile(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = input.files && input.files.length > 0 ? input.files[0] : null;
    input.value = '';

    if (selected && !isImageAttachment(selected)) {
      this.pendingFile = null;
      this.startError = IMAGE_ONLY_MESSAGE;
      return;
    }
    this.pendingFile = selected;
  }

  clearFile(): void {
    this.pendingFile = null;
  }

  isImage(mimeType: string | null): boolean {
    return !!mimeType && mimeType.startsWith('image/');
  }

  start(): void {
    this.startError = '';
    const name = this.guestName.trim();
    const email = this.guestEmail.trim();
    const text = this.newMessageText.trim();
    if (!name || !email || !text) {
      this.startError = 'Please fill in your name, email and question.';
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      this.startError = 'Please enter a valid email address.';
      return;
    }
    if (containsProfanity(text) || containsProfanity(name)) {
      this.startError = PROFANITY_MESSAGE;
      return;
    }
    if (containsLink(text) || containsLink(name)) {
      this.startError = LINK_MESSAGE;
      return;
    }
    this.sending = true;
    this.registrationInquiryService.startInquiry(name, email, text).subscribe({
      next: (result) => {
        this.inquiryId = result.inquiryId;
        this.guestToken = result.guestToken;
        guestChatStorage.setItem(INQUIRY_ID_KEY, result.inquiryId);
        guestChatStorage.setItem(INQUIRY_TOKEN_KEY, result.guestToken);
        guestChatStorage.setItem(GUEST_NAME_KEY, name);
        this.newMessageText = '';
        this.sending = false;
        this.refresh();
        this.startPolling();
      },
      error: () => {
        this.sending = false;
        this.startError = 'Unable to send your question right now. Please try again.';
      },
    });
  }

  send(): void {
    const text = this.newMessageText.trim();
    if ((!text && !this.pendingFile) || !this.hasInquiry || this.sending) return;
    if (containsProfanity(text)) {
      this.startError = PROFANITY_MESSAGE;
      return;
    }
    if (containsLink(text)) {
      this.startError = LINK_MESSAGE;
      return;
    }
    this.sending = true;
    this.emojiOpen = false;
    this.registrationInquiryService
      .sendMessage(this.inquiryId!, this.guestToken!, text, this.pendingFile)
      .subscribe({
        next: (message) => {
          this.appendMessage(message);
          this.newMessageText = '';
          this.pendingFile = null;
          this.sending = false;
          this.startError = '';
          this.scrollToBottom();
        },
        error: () => {
          this.sending = false;
          this.startError = 'Unable to send the message. Please try again.';
        },
      });
  }

  private refresh(): void {
    if (!this.hasInquiry) return;
    this.registrationInquiryService
      .getMessages(this.inquiryId!, this.guestToken!)
      .subscribe({
        next: (messages) => {
          const grew = messages.length > this.messages.length;
          this.messages = messages;
          if (this.open) {
            this.markRead();
            if (grew) this.scrollToBottom();
          } else {
            this.unreadCount = messages.filter(
              (m) => m.senderSide === 'Staff' && !m.isReadByGuest,
            ).length;
          }
        },
        error: () => {
          // A stale/cleared inquiry — start fresh next time.
        },
      });
  }

  private markRead(): void {
    if (!this.hasInquiry) return;
    if (!this.messages.some((m) => m.senderSide === 'Staff' && !m.isReadByGuest)) {
      this.unreadCount = 0;
      return;
    }
    this.registrationInquiryService
      .markMessagesRead(this.inquiryId!, this.guestToken!)
      .subscribe({ next: () => (this.unreadCount = 0) });
  }

  private appendMessage(message: RegistrationInquiryMessage): void {
    if (this.messages.some((m) => m.id === message.id)) return;
    this.messages = [...this.messages, message];
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messageScroll) {
        this.messageScroll.nativeElement.scrollTop = this.messageScroll.nativeElement.scrollHeight;
      }
    });
  }

  private startPolling(): void {
    this.stopPolling();
    const interval = this.open && !document.hidden ? this.openPollIntervalMs : this.closedPollIntervalMs;
    this.pollHandle = setInterval(() => this.refresh(), interval);
  }

  private stopPolling(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }
}
